import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {
  getHotelOfferEntitiesHaveOffers,
  getModuleState
} from 'modules/sapiSearch/selectors'
import {ModuleState} from 'modules/sapiSearch/slice'

import {Alert} from '@vio/atlas/Alert'
import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {useToastController} from '@vio/atlas/context/toastController'
import {Box} from '@vio/atlas/helpers/Box'
import OpacityAnimation from '@vio/atlas/helpers/OpacityAnimation'
import {Icon} from '@vio/atlas/Icon'
import {Link} from '@vio/atlas/Link'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

interface MessageProps {
  isMobile?: boolean
  onTryAgain: () => void
}

const Wrapper = styled.div`
  padding: 0 0 ${cssTheme.layout.spacing.s600} 0;
`

const Title = styled.div`
  ${cssTheme.typography.titleM}
  ${linariaMq.desktopXs} {
    ${cssTheme.typography.titleS}
  }
`

const RetryButton = styled(Button)`
  margin-top: ${cssTheme.layout.spacing.s300};
`

const TimeoutMessage: React.FC<MessageProps> = ({
  isMobile = false,
  onTryAgain
}) => {
  return (
    <Box display="flex" gap="s100" flexWrap="wrap">
      <Title>
        <FormattedMessageWrapper
          id="backendError.title"
          defaultMessage="Something went wrong"
        />
        {!isMobile && <span>.</span>}
      </Title>
      <FormattedMessageWrapper
        id="gettingResultsError"
        defaultMessage="We're having trouble getting the latest results."
      />
      {isMobile ? (
        <RetryButton
          variant="tertiary"
          fullWidth={true}
          size="lg"
          onClick={onTryAgain}
        >
          <FormattedMessageWrapper id="tryAgain" defaultMessage="Try Again" />
        </RetryButton>
      ) : (
        <Link variant="primary" href="#" onClick={onTryAgain}>
          <FormattedMessageWrapper id="tryAgain" defaultMessage="Try Again" />
        </Link>
      )}
    </Box>
  )
}

const SEARCH_TIMEOUT = 20000 // 20 seconds

export const SearchResultAlert = () => {
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const {isMobile} = useDeviceLayout()
  const {open, close} = useToastController()
  const dispatch = useDispatch()
  const [isVisible, setIsVisible] = useState(false)
  const moduleState = useSelector(getModuleState)
  const hasOffers = useSelector(getHotelOfferEntitiesHaveOffers)
  const timeout = useRef(null)

  const handleRefresh = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SearchResultTimeoutAlertTryAgainButton,
        action: Action.Clicked,
        team: Team.Search
      })
    )

    setTimeout(
      () => window.location.reload(),
      TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  useEffect(() => {
    // clear the timeout if the search is completed or if we have received some offers
    if (
      moduleState === ModuleState.SearchCompleted ||
      // we don't want to show the alert if we have received at least the first batch of offers
      hasOffers ||
      isDatelessSeoSRP
    ) {
      clearTimeout(timeout.current)
      timeout.current = null
    } else if (!timeout.current) {
      setIsVisible(false)
      timeout.current = setTimeout(() => setIsVisible(true), SEARCH_TIMEOUT)
    }
  }, [moduleState, isVisible, hasOffers, isDatelessSeoSRP])

  useEffect(() => {
    if (isVisible) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.SearchResultTimeoutAlert,
          action: Action.Displayed,
          team: Team.Search
        })
      )
    }
  }, [isVisible, dispatch])

  useEffect(() => {
    if (isMobile) {
      if (isVisible) {
        open(
          <TimeoutMessage isMobile={isMobile} onTryAgain={handleRefresh} />,
          {
            icon: <Icon name="WarningTriangle" />,
            showCloseButton: false,
            singleToastMessage: true,
            autoClose: false
          }
        )
      } else {
        close()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, dispatch, isMobile])

  return (
    isVisible &&
    !isMobile && (
      <OpacityAnimation delay={1500} duration={0.5}>
        <Wrapper>
          <Alert variant="danger" icon={<Icon name="WarningTriangle" />}>
            <TimeoutMessage onTryAgain={handleRefresh} />
          </Alert>
        </Wrapper>
      </OpacityAnimation>
    )
  )
}
